import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import {
  ArrowLeft,
  Eye,
  UploadCloud,
  Loader2,
  Trash2,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Link2,
  RefreshCw,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, mediaUrl } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp";

export default function EditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [presentation, setPresentation] = useState(null);
  const [slides, setSlides] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  // Reorder.Item's onDragEnd runs after this render, so persist the freshest
  // list through a ref instead of the state value the handler was created with.
  const slidesRef = useRef([]);

  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/presentations/${id}`);
      setPresentation(data);
      setSlides(data.slides || []);
      setTitle(data.title);
      setDescription(data.description || "");
    } catch (e) {
      toast.error(apiError(e, "Failed to load presentation"));
      navigate("/admin");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const { data } = await api.patch(`/presentations/${id}`, { title, description });
      setPresentation((p) => ({ ...p, ...data }));
      toast.success("Saved");
    } catch (e) {
      toast.error(apiError(e, "Could not save"));
    } finally {
      setSavingMeta(false);
    }
  };

  const togglePublic = async (value) => {
    try {
      const { data } = await api.patch(`/presentations/${id}`, { is_public: value });
      setPresentation((p) => ({ ...p, is_public: data.is_public }));
      toast.success(value ? "Presentation is now public" : "Presentation is now private");
    } catch (e) {
      toast.error(apiError(e, "Could not update visibility"));
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) {
      toast.error("Please choose image files only");
      return;
    }
    setUploading(true);
    setProgress(0);
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    try {
      const { data } = await api.post(`/presentations/${id}/slides`, form, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      setSlides((prev) => [...prev, ...data]);
      toast.success(`${data.length} slide${data.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      toast.error(apiError(e, "Upload failed"));
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const persistOrder = useCallback(
    async (list) => {
      try {
        await api.patch(`/presentations/${id}/slides/reorder`, {
          slide_ids: list.map((s) => s.id),
        });
      } catch (e) {
        toast.error(apiError(e, "Could not save order"));
      }
    },
    [id]
  );

  const moveSlide = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= slides.length) return;
    const next = [...slides];
    [next[index], next[target]] = [next[target], next[index]];
    setSlides(next);
    persistOrder(next);
  };

  const deleteSlide = async (slideId) => {
    try {
      await api.delete(`/presentations/${id}/slides/${slideId}`);
      setSlides((prev) => prev.filter((s) => s.id !== slideId));
      toast.success("Slide removed");
    } catch (e) {
      toast.error(apiError(e, "Could not delete slide"));
    }
  };

  const regenerateSlug = async () => {
    try {
      const { data } = await api.post(`/presentations/${id}/regenerate-slug`);
      setPresentation((p) => ({ ...p, slug: data.slug }));
      toast.success("New share link generated (old link revoked)");
    } catch (e) {
      toast.error(apiError(e, "Could not regenerate link"));
    }
  };

  const shareUrl = presentation
    ? `${window.location.origin}/ppt`
    : "";

  const copyShare = async () => {
    const ok = await copyText(shareUrl);
    if (!ok) {
      toast.error("Couldn't copy automatically. Link: " + shareUrl);
      return;
    }
    if (presentation?.is_public) toast.success("Public link copied");
    else toast.warning("Copied — enable public access so viewers can open it");
  };

  if (!presentation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-brand-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background bg-grid">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-white/10">
        <div className="max-w-screen-xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin")}
              className="rounded-full text-muted-foreground hover:text-white hover:bg-white/10 shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Logo className="h-6 hidden sm:block" />
          </div>
          <Button
            data-testid="editor-preview-btn"
            onClick={() => navigate(`/admin/presentations/${id}/preview`)}
            disabled={slides.length === 0}
            className="rounded-full bg-brand-blue hover:bg-brand-bluehover text-white"
          >
            <Eye className="h-4 w-4" /> Preview
          </Button>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <section className="rounded-2xl border border-white/10 bg-card p-6">
            <h2 className="font-heading text-lg font-semibold text-white mb-4">Details</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  Presentation name
                </Label>
                <Input
                  data-testid="editor-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="bg-background/60 border-white/10 h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  Description
                </Label>
                <Textarea
                  data-testid="editor-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="bg-background/60 border-white/10"
                />
              </div>
              <Button
                data-testid="save-meta-btn"
                onClick={saveMeta}
                disabled={savingMeta}
                variant="secondary"
                className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white w-full"
              >
                {savingMeta ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save details</>}
              </Button>
            </div>
          </section>

          <section className="rounded-2xl border border-white/10 bg-card p-6">
            <h2 className="font-heading text-lg font-semibold text-white mb-1">Share</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Make this presentation public to share it with anyone — no login required.
            </p>

            <div className="flex items-center justify-between rounded-xl bg-background/50 border border-white/10 px-4 py-3 mb-4">
              <div>
                <div className="text-sm font-medium text-white">Public access</div>
                <div className="text-xs text-muted-foreground">
                  {presentation.is_public ? "Anyone with the link can view" : "Only you can view"}
                </div>
              </div>
              <Switch
                data-testid="public-toggle"
                checked={presentation.is_public}
                onCheckedChange={togglePublic}
              />
            </div>

            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
              Share link
            </Label>
            <div className="mt-2 flex items-center gap-2">
              <Input
                data-testid="share-url-input"
                readOnly
                value={shareUrl}
                className="bg-background/60 border-white/10 h-11 text-xs"
              />
              <Button
                data-testid="copy-share-btn"
                onClick={copyShare}
                size="icon"
                className="rounded-full bg-brand-blue hover:bg-brand-bluehover text-white shrink-0 h-11 w-11"
              >
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
            <button
              data-testid="regenerate-link-btn"
              onClick={regenerateSlug}
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-white transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Revoke &amp; generate new link
            </button>
          </section>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <section
            data-testid="upload-zone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              uploadFiles(e.dataTransfer.files);
            }}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
              dragOver ? "border-brand-blue bg-brand-blue/10" : "border-white/15 bg-white/[0.02] hover:border-white/30"
            }`}
          >
            <input
              ref={fileInputRef}
              data-testid="file-input"
              type="file"
              accept={ACCEPT}
              multiple
              hidden
              onChange={(e) => uploadFiles(e.target.files)}
            />
            <div className="h-14 w-14 mx-auto rounded-2xl bg-brand-blue/10 border border-brand-blue/20 grid place-items-center mb-4">
              <UploadCloud className="h-7 w-7 text-brand-blue" />
            </div>
            <div className="font-heading text-lg font-semibold text-white">
              Drag &amp; drop images here
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              or click to browse · JPG, PNG, WebP, GIF · high-resolution supported
            </p>
            {uploading && (
              <div className="mt-5 max-w-sm mx-auto">
                <Progress value={progress} className="h-2 bg-white/10" />
                <div className="text-xs text-muted-foreground mt-2">Uploading… {progress}%</div>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-white/10 bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold text-white">
                Slides <span className="text-muted-foreground font-normal">({slides.length})</span>
              </h2>
              {slides.length > 1 && (
                <span className="text-xs text-muted-foreground">Drag to reorder</span>
              )}
            </div>

            {slides.length === 0 ? (
              <div className="text-center py-14 text-muted-foreground text-sm">
                No slides yet. Upload images above to build your presentation.
              </div>
            ) : (
              <Reorder.Group
                axis="y"
                values={slides}
                onReorder={setSlides}
                className="space-y-3"
                data-testid="slides-list"
              >
                {slides.map((slide, index) => (
                  <SlideRow
                    key={slide.id}
                    slide={slide}
                    index={index}
                    total={slides.length}
                    onDragEnd={() => persistOrder(slidesRef.current)}
                    onMove={moveSlide}
                    onDelete={deleteSlide}
                  />
                ))}
              </Reorder.Group>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function SlideRow({ slide, index, total, onDragEnd, onMove, onDelete }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={slide}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragEnd}
      data-testid={`slide-row-${index}`}
      className="flex items-center gap-4 rounded-xl border border-white/10 bg-background/50 p-3"
    >
      <div
        onPointerDown={(e) => controls.start(e)}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-white touch-none"
        data-testid={`drag-handle-${index}`}
      >
        <GripVertical className="h-5 w-5" />
      </div>
      <div className="font-heading text-sm font-semibold text-brand-blue w-6 text-center tabular-nums">
        {index + 1}
      </div>
      <div className="h-16 w-24 rounded-lg overflow-hidden bg-black/40 shrink-0 grid place-items-center">
        <img
          src={mediaUrl(slide.thumbnail_url)}
          alt={slide.original_filename || `Slide ${index + 1}`}
          className="max-w-full max-h-full object-contain"
          draggable={false}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate">
          {slide.original_filename || `Slide ${index + 1}`}
        </div>
        <div className="text-xs text-muted-foreground">
          {slide.width && slide.height ? `${slide.width} × ${slide.height}` : "Image"}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          data-testid={`move-up-${index}`}
          variant="ghost"
          size="icon"
          disabled={index === 0}
          onClick={() => onMove(index, -1)}
          className="rounded-full text-muted-foreground hover:text-white hover:bg-white/10 disabled:opacity-20 h-8 w-8"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          data-testid={`move-down-${index}`}
          variant="ghost"
          size="icon"
          disabled={index === total - 1}
          onClick={() => onMove(index, 1)}
          className="rounded-full text-muted-foreground hover:text-white hover:bg-white/10 disabled:opacity-20 h-8 w-8"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button
          data-testid={`delete-slide-${index}`}
          variant="ghost"
          size="icon"
          onClick={() => onDelete(slide.id)}
          className="rounded-full text-muted-foreground hover:text-red-400 hover:bg-red-500/10 h-8 w-8"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </Reorder.Item>
  );
}
